export type TextSegment =
  | {
      id: string;
      type: "text";
      value: string;
    }
  | {
      id: string;
      type: "url";
      value: string;
      href: string;
    };

const URL_CANDIDATE_PATTERN = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;
const SIMPLE_TRAILING_PUNCTUATION = new Set([".", ",", "!", "?", ";", ":"]);
const CLOSING_TO_OPENING_BRACKET = new Map([
  [")", "("],
  ["]", "["],
  ["}", "{"],
]);

export function parseUrlSegments(text: string): TextSegment[] {
  const segments: TextSegment[] = [];
  let cursor = 0;

  for (const match of text.matchAll(URL_CANDIDATE_PATTERN)) {
    const rawCandidate = match[0];
    const matchIndex = match.index ?? 0;

    pushTextSegment(segments, text.slice(cursor, matchIndex));

    const { urlText, trailingText } = splitTrailingText(rawCandidate);
    const href = normalizeHttpUrl(urlText);

    if (href) {
      pushUrlSegment(segments, urlText, href);
      pushTextSegment(segments, trailingText);
    } else {
      pushTextSegment(segments, rawCandidate);
    }

    cursor = matchIndex + rawCandidate.length;
  }

  pushTextSegment(segments, text.slice(cursor));

  return segments;
}

function pushTextSegment(segments: TextSegment[], value: string) {
  if (!value) return;

  const previousSegment = segments.at(-1);
  if (previousSegment?.type === "text") {
    previousSegment.value += value;
    return;
  }

  segments.push({
    id: `text-segment-${segments.length}`,
    type: "text",
    value,
  });
}

function pushUrlSegment(segments: TextSegment[], value: string, href: string) {
  segments.push({
    id: `url-segment-${segments.length}`,
    type: "url",
    value,
    href,
  });
}

function normalizeHttpUrl(value: string): string | null {
  try {
    const url = new URL(value.startsWith("www.") ? `https://${value}` : value);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;

    return url.href;
  } catch (_error) {
    return null;
  }
}

function splitTrailingText(value: string) {
  let urlText = value;
  let trailingText = "";

  while (urlText.length > 0) {
    const lastChar = urlText.at(-1);
    if (!lastChar) break;

    if (SIMPLE_TRAILING_PUNCTUATION.has(lastChar)) {
      urlText = urlText.slice(0, -1);
      trailingText = `${lastChar}${trailingText}`;
      continue;
    }

    const openingBracket = CLOSING_TO_OPENING_BRACKET.get(lastChar);
    if (
      openingBracket &&
      countOccurrences(urlText, lastChar) >
        countOccurrences(urlText, openingBracket)
    ) {
      urlText = urlText.slice(0, -1);
      trailingText = `${lastChar}${trailingText}`;
      continue;
    }

    break;
  }

  return { urlText, trailingText };
}

function countOccurrences(value: string, char: string) {
  return [...value].filter((currentChar) => currentChar === char).length;
}
